import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency, calculatePayroll } from '../../utils/payrollCalculations';
import { getApiUrl } from '../../config/api';
import { usePayrollData } from '../../hooks/usePayrollData';
import { usePayrollActions, PayrollRecord } from '../../hooks/usePayrollActions';
import { useAutoDismiss } from '../../hooks/useAutoDismiss';
import PayrollFormModal from '../../components/PayrollFormModal';
import PayrollTable from '../../components/PayrollTable';
import PayslipPreviewModal from '../../components/PayslipPreviewModal';

const PAYROLL_ROLE_FILTERS = [
  { key: 'all', label: 'All Staff' },
  { key: 'teaching', label: 'Teaching Staff' },
  { key: 'administrative', label: 'Administrative Staff' },
  { key: 'workers', label: 'Non-Teaching Workers' }
] as const;

const PayrollManagement: React.FC = () => {
  const navigate = useNavigate();

  const {
    teachers,
    staffMembers,
    periods,
    payrolls,
    loading: dataLoading,
    error,
    success,
    page,
    search,
    selectedPeriod,
    selectedStatus,
    staffCategory,
    totalPages,
    totalRecords,
    setPage,
    setSearch,
    setSelectedPeriod,
    setSelectedStatus,
    setStaffCategory,
    setError,
    setSuccess,
    fetchData
  } = usePayrollData();

  const {
    loading: actionLoading,
    handleDelete,
    handleBulkDelete,
    handleMarkAsPaid,
    handleApprove
  } = usePayrollActions(
    fetchData,
    (error: string | null) => setError(error),
    (message: string | null) => setSuccess(message)
  );

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState<any | null>(null);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState<any | null>(null);
  const [bulkPeriodId, setBulkPeriodId] = useState('');
  const [bulkPayslipLoading, setBulkPayslipLoading] = useState(false);
  const [formData, setFormData] = useState({
    teacherId: '',
    periodId: '',
    payPeriod: '',
    baseSalary: '',
    housingAllowance: '',
    transportAllowance: '',
    responsibilityAllowance: '',
    otherAllowances: '',
    daysAbsentDeduction: '0',
    loanDeduction: '0',
    rentDeduction: '0',
    schoolFeesDeduction: '0',
    personalLevy: '0',
    otherDeductions: '0',
  });
  const [loading, setLoading] = useState(false);

  useAutoDismiss(error, setError, null, 5000);
  useAutoDismiss(success, setSuccess, null, 5000);

  const matchesStaffCategory = (role?: string) => {
    const normalized = String(role || '').trim().toLowerCase();
    if (staffCategory === 'teaching') return normalized === 'teacher';
    if (staffCategory === 'administrative') return normalized === 'admin' || normalized === 'headteacher';
    if (staffCategory === 'workers') return normalized === 'worker';
    return true;
  };

  const filteredStaffMembers = useMemo(
    () => staffMembers.filter((staffMember) => matchesStaffCategory(staffMember.role)),
    [staffMembers, staffCategory]
  );

  const filteredPayrolls = payrolls;

  // Calculate totals for CURRENT PAGE data
  const totals = useMemo(() => {
    return {
      grossSalary: filteredPayrolls.reduce((sum, p) => sum + Number(p.grossSalary || 0), 0),
      totalDeductions: filteredPayrolls.reduce((sum, p) => sum + Number(p.totalDeductions || 0), 0),
      netSalary: filteredPayrolls.reduce((sum, p) => sum + Number(p.netSalary || 0), 0),
      napsa: filteredPayrolls.reduce((sum, p) => sum + Number(p.napsaDeduction || 0), 0),
      paye: filteredPayrolls.reduce((sum, p) => sum + Number(p.payeDeduction || 0), 0),
      nhima: filteredPayrolls.reduce((sum, p) => sum + Number(p.nhimaDeduction || 0), 0),
      count: filteredPayrolls.length,
    };
  }, [filteredPayrolls]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on new search
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.teacherId || !formData.periodId || !formData.baseSalary) {
      setError('Required fields missing');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token') || '';
      const salaryComponents = {
        baseSalary: parseFloat(formData.baseSalary) || 0,
        housingAllowance: parseFloat(formData.housingAllowance) || 0,
        transportAllowance: parseFloat(formData.transportAllowance) || 0,
        responsibilityAllowance: parseFloat(formData.responsibilityAllowance) || 0,
        otherAllowances: parseFloat(formData.otherAllowances) || 0,
      };

      const deductionComponents = {
        daysAbsentDeduction: parseFloat(formData.daysAbsentDeduction) || 0,
        advanceSalaryRepayment: 0,
        loanRepayment: parseFloat(formData.loanDeduction) || 0,
        rentDeduction: parseFloat(formData.rentDeduction) || 0,
        schoolFeesDeduction: parseFloat(formData.schoolFeesDeduction) || 0,
        personalLevy: parseFloat(formData.personalLevy) || 0,
        otherDeductions: parseFloat(formData.otherDeductions) || 0,
      };

      const result = calculatePayroll(salaryComponents, deductionComponents);

      const payload = {
        teacherId: parseInt(formData.teacherId),
        periodId: parseInt(formData.periodId),
        payPeriod: formData.payPeriod,
        baseSalary: salaryComponents.baseSalary,
        housingAllowance: salaryComponents.housingAllowance,
        transportAllowance: salaryComponents.transportAllowance,
        responsibilityAllowance: salaryComponents.responsibilityAllowance,
        otherAllowances: salaryComponents.otherAllowances,
        grossSalary: result.grossSalary,
        totalDeductions: result.totalDeductions,
        netSalary: result.netSalary,
        napsaDeduction: result.napsaDeduction,
        payeDeduction: result.payeDeduction,
        nhimaDeduction: result.nhimaDeduction,
        daysAbsentDeduction: deductionComponents.daysAbsentDeduction,
        loanDeduction: deductionComponents.loanRepayment,
        rentDeduction: deductionComponents.rentDeduction,
        schoolFeesDeduction: deductionComponents.schoolFeesDeduction,
        personalLevyDeduction: deductionComponents.personalLevy,
        otherDeductions: deductionComponents.otherDeductions,
        status: editingPayroll?.status || 'generated',
      };

      if (editingPayroll) {
        await axios.put(getApiUrl(`payroll/${editingPayroll.id}`), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Payroll updated successfully');
      } else {
        await axios.post(getApiUrl('payroll'), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Payroll created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (!bulkPeriodId) {
      setError('Please select a period to generate payslips for.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token') || '';
      await axios.post(getApiUrl(`payroll/generate-payslips/${bulkPeriodId}`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Payslips generated successfully for all eligible staff.');
      setShowBulkModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error generating bulk payslips');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffMemberChange = (id: string) => {
    const staffMember = filteredStaffMembers.find((member) => member.id.toString() === id) || staffMembers.find((member) => member.id.toString() === id);
    setFormData({
      ...formData,
      teacherId: id,
      baseSalary: staffMember?.baseSalary ? staffMember.baseSalary.toString() : formData.baseSalary,
      housingAllowance: staffMember?.housingAllowance !== undefined ? String(staffMember.housingAllowance) : formData.housingAllowance,
      transportAllowance: staffMember?.transportAllowance !== undefined ? String(staffMember.transportAllowance) : formData.transportAllowance,
      responsibilityAllowance: staffMember?.responsibilityAllowance !== undefined ? String(staffMember.responsibilityAllowance) : formData.responsibilityAllowance,
      otherAllowances: staffMember?.otherAllowances !== undefined ? String(staffMember.otherAllowances) : formData.otherAllowances
    });
  };

  const handleEdit = (payroll: PayrollRecord) => {
    setEditingPayroll(payroll);
    setFormData({
      teacherId: payroll.teacherId.toString(),
      periodId: payroll.periodId.toString(),
      payPeriod: (payroll as any).payPeriod || 'Monthly',
      baseSalary: Number(payroll.baseSalary || 0).toString(),
      housingAllowance: Number(payroll.housingAllowance || 0).toString(),
      transportAllowance: Number(payroll.transportAllowance || 0).toString(),
      responsibilityAllowance: Number(payroll.responsibilityAllowance || 0).toString(),
      otherAllowances: Number(payroll.otherAllowances || 0).toString(),
      daysAbsentDeduction: Number((payroll as any).daysAbsentDeduction || 0).toString(),
      loanDeduction: Number((payroll as any).loanDeduction || 0).toString(),
      rentDeduction: Number((payroll as any).rentDeduction || 0).toString(),
      schoolFeesDeduction: Number((payroll as any).schoolFeesDeduction || 0).toString(),
      personalLevy: Number((payroll as any).personalLevyDeduction || 0).toString(),
      otherDeductions: Number((payroll as any).otherDeductions || 0).toString(),
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingPayroll(null);
    setFormData({
      teacherId: '',
      periodId: '',
      payPeriod: 'Monthly',
      baseSalary: '',
      housingAllowance: '0',
      transportAllowance: '0',
      responsibilityAllowance: '0',
      otherAllowances: '0',
      daysAbsentDeduction: '0',
      loanDeduction: '0',
      rentDeduction: '0',
      schoolFeesDeduction: '0',
      personalLevy: '0',
      otherDeductions: '0',
    });
  };

  const viewPayslip = (payroll: PayrollRecord) => {
    setSelectedPayslip(payroll);
    setShowPayslipModal(true);
  };

  const handleExportCSV = () => {
    const headers = ['Employee', 'NRC', 'Period', 'Base Salary', 'Gross Salary', 'Deductions', 'Net Salary', 'Status'];
    const rows = payrolls.map(p => [
      p.teacher?.name || 'Unknown',
      p.teacher?.nrcNumber || 'N/A',
      p.period?.name || 'Unknown',
      p.baseSalary,
      p.grossSalary,
      p.totalDeductions,
      p.netSalary,
      p.status,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getPayslipExportParams = () => {
    const params: Record<string, string> = {};
    if (search.trim()) {
      params.search = search.trim();
    }
    if (selectedPeriod !== 'all') {
      params.periodId = selectedPeriod;
    }
    if (selectedStatus !== 'all') {
      params.status = selectedStatus;
    }
    if (staffCategory !== 'all') {
      params.staffCategory = staffCategory;
    }
    return params;
  };

  const fetchFilteredPayslipsPdf = async () => {
    const token = localStorage.getItem('token') || '';
    return axios.get(getApiUrl('payroll/payslips/export/pdf'), {
      headers: { Authorization: `Bearer ${token}` },
      params: getPayslipExportParams(),
      responseType: 'blob'
    });
  };

  const handleDownloadFilteredPayslips = async () => {
    try {
      setBulkPayslipLoading(true);
      setError(null);
      const response = await fetchFilteredPayslipsPdf();
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      link.href = blobUrl;
      link.download = filenameMatch?.[1] || `payslips_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download filtered payslips');
    } finally {
      setBulkPayslipLoading(false);
    }
  };

  const handlePrintFilteredPayslips = async () => {
    try {
      setBulkPayslipLoading(true);
      setError(null);
      const response = await fetchFilteredPayslipsPdf();
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(blobUrl, '_blank');

      if (!printWindow) {
        window.URL.revokeObjectURL(blobUrl);
        throw new Error('Popup blocked while trying to print payslips');
      }

      const triggerPrint = () => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (printError) {
          console.error('Print failed:', printError);
        }
      };

      printWindow.addEventListener('load', triggerPrint, { once: true });
      window.setTimeout(triggerPrint, 1200);
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to print filtered payslips');
    } finally {
      setBulkPayslipLoading(false);
    }
  };

  if (dataLoading && payrolls.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h1 className="text-2xl font-bold">Staff Payroll</h1>
        <div className="flex gap-2 mt-4 md:mt-0">
          <button
            onClick={handlePrintFilteredPayslips}
            disabled={bulkPayslipLoading || totalRecords === 0}
            className="bg-slate-700 text-white px-4 py-2 rounded hover:bg-slate-800 disabled:opacity-50"
          >
            {bulkPayslipLoading ? 'Preparing...' : 'Print Payslips'}
          </button>
          <button
            onClick={handleDownloadFilteredPayslips}
            disabled={bulkPayslipLoading || totalRecords === 0}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {bulkPayslipLoading ? 'Preparing...' : 'Download Payslips'}
          </button>
          <button
            onClick={handleExportCSV}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Export CSV
          </button>
          <button
            onClick={() => setShowBulkModal(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 font-bold"
          >
            Bulk Generate
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add Single Payroll
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Employee</label>
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={handleSearchChange}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pay Period</label>
            <select
              value={selectedPeriod}
              onChange={(e) => {
                setSelectedPeriod(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2"
            >
              <option value="all">All Periods</option>
              {periods.map(period => (
                <option key={period.id} value={period.id}>{period.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="generated">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div className="min-w-[18rem]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Staff Category</label>
            <div className="flex flex-wrap gap-2">
              {PAYROLL_ROLE_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => {
                    setStaffCategory(filter.key);
                    setPage(1);
                  }}
                  className={`rounded-full border px-3 py-2 text-sm font-medium transition ${
                    staffCategory === filter.key
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ml-auto text-right">
            <p className="text-sm text-gray-600">Total Filtered</p>
            <p className="text-xl font-bold text-gray-900">{totalRecords} Records</p>
          </div>
        </div>
      </div>

      {/* Summary Cards (Note: These show totals for CURRENT PAGE) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-xs text-gray-600">Page Gross</p>
          <p className="text-lg font-bold text-blue-600">{formatCurrency(totals.grossSalary)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-xs text-gray-600">Page Deductions</p>
          <p className="text-lg font-bold text-red-600">{formatCurrency(totals.totalDeductions)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-xs text-gray-600">Page Net</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(totals.netSalary)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-xs text-gray-600">NAPSA</p>
          <p className="text-sm font-bold text-orange-600">{formatCurrency(totals.napsa)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-xs text-gray-600">PAYE</p>
          <p className="text-sm font-bold text-purple-600">{formatCurrency(totals.paye)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <p className="text-xs text-gray-600">NHIMA</p>
          <p className="text-sm font-bold text-indigo-600">{formatCurrency(totals.nhima)}</p>
        </div>
      </div>

      {/* Payroll Records Table Component */}
      <PayrollTable
        payrolls={filteredPayrolls}
        page={page}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={setPage}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkDelete={handleBulkDelete}
        onMarkAsPaid={handleMarkAsPaid}
        onApprove={handleApprove}
        onViewPayslip={viewPayslip}
        onRefresh={fetchData}
      />

      {/* Add/Edit Payroll Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingPayroll ? 'Edit Staff Payroll' : 'Add Staff Payroll'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Employee <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.teacherId}
                    onChange={(e) => handleStaffMemberChange(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="">Select Employee</option>
                    {filteredStaffMembers.map((staffMember) => (
                      <option key={staffMember.id} value={staffMember.id}>
                        {staffMember.name}
                        {staffMember.role === 'worker' && staffMember.jobTitle ? ` (${staffMember.jobTitle})` : ''}
                        {staffMember.baseSalary ? ` (Basis: ${formatCurrency(staffMember.baseSalary)})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Payroll Period (Month/Year) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.periodId}
                    onChange={(e) => setFormData({ ...formData, periodId: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="">Select Period...</option>
                    {periods.map(period => (
                      <option key={period.id} value={period.id}>
                        {period.name}{period.isLocked ? ' (Locked)' : ''}
                      </option>
                    ))}
                  </select>
                  {periods.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠ No periods found. Create payroll periods first.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Pay Period Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.payPeriod}
                    onChange={(e) => setFormData({ ...formData, payPeriod: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="">Select Pay Period...</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-Weekly">Bi-Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Fortnightly">Fortnightly</option>
                    <option value="Quarterly">Quarterly</option>
                  </select>
                </div>
              </div>

              <h3 className="font-medium mb-2 text-blue-600">Earnings</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Base Salary *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.baseSalary}
                    onChange={(e) => setFormData({ ...formData, baseSalary: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Housing Allowance</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.housingAllowance}
                    onChange={(e) => setFormData({ ...formData, housingAllowance: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Transport Allowance</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.transportAllowance}
                    onChange={(e) => setFormData({ ...formData, transportAllowance: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Responsibility Allowance</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.responsibilityAllowance}
                    onChange={(e) => setFormData({ ...formData, responsibilityAllowance: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Other Allowances</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.otherAllowances}
                    onChange={(e) => setFormData({ ...formData, otherAllowances: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              </div>

              <h3 className="font-medium mb-2 text-red-600">Deductions</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Days Absent Deduction</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.daysAbsentDeduction}
                    onChange={(e) => setFormData({ ...formData, daysAbsentDeduction: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Loan Repayment</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.loanDeduction}
                    onChange={(e) => setFormData({ ...formData, loanDeduction: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Rent Deduction</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.rentDeduction}
                    onChange={(e) => setFormData({ ...formData, rentDeduction: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">School Fees Deduction</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.schoolFeesDeduction}
                    onChange={(e) => setFormData({ ...formData, schoolFeesDeduction: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Personal Levy</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.personalLevy}
                    onChange={(e) => setFormData({ ...formData, personalLevy: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Other Deductions</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.otherDeductions}
                    onChange={(e) => setFormData({ ...formData, otherDeductions: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              </div>

              <div className="bg-gray-100 p-3 rounded mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> NAPSA, PAYE, and NHIMA will be calculated automatically based on Zambia tax regulations.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Generate Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h2 className="text-xl font-bold mb-4">Bulk Generate Payslips</h2>
            <p className="text-gray-600 mb-4 text-sm">
              This will automatically calculate and create payslips for all staff members who do not already have one for the selected payroll period.
            </p>
            <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              You can generate for any saved payroll period, including past periods you created later and future periods prepared in advance. If the period is not listed here, create it first under Payroll Periods.
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Select Payroll Period</label>
              <select
                value={bulkPeriodId}
                onChange={(e) => setBulkPeriodId(e.target.value)}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option value="">Select Period...</option>
                {periods.map(period => (
                  <option key={period.id} value={period.id}>
                    {period.name} {period.status === 'open' ? '(Open)' : `(${period.status})`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkGenerate}
                disabled={loading || !bulkPeriodId}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 font-bold"
              >
                {loading ? 'Generating...' : 'Start Bulk Generation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payslip Preview Modal */}
      {showPayslipModal && selectedPayslip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Payslip Preview</h2>
              <button
                onClick={() => setShowPayslipModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="border rounded p-4 bg-gray-50">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">Employee</p>
                  <p className="font-medium">{selectedPayslip.teacher?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Period</p>
                  <p className="font-medium">{selectedPayslip.period?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Gross Salary</p>
                  <p className="font-medium text-green-600">{formatCurrency(Number(selectedPayslip.grossSalary || 0))}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Deductions</p>
                  <p className="font-medium text-red-600">({formatCurrency(Number(selectedPayslip.totalDeductions || 0))})</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Net Salary</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(Number(selectedPayslip.netSalary || 0))}</p>
                </div>
              </div>
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-gray-500">
                  NAPSA: {formatCurrency(Number(selectedPayslip.napsaDeduction || 0))} |
                  PAYE: {formatCurrency(Number(selectedPayslip.payeDeduction || 0))} |
                  NHIMA: {formatCurrency(Number(selectedPayslip.nhimaDeduction || 0))} |
                  Levy: {formatCurrency(Number(selectedPayslip.personalLevyDeduction || 0))}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowPayslipModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Close
              </button>
              <button
                onClick={() => navigate(`/admin/payslip/${selectedPayslip.id}`)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                View Full Payslip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollManagement;



